import {
  getFocusable,
  handleModalKeydown,
  setModalOpen
} from "components/supplier_admin/shared/modal_helpers";

const DEFAULT_ERROR_MESSAGE = "Failed to finish the print job. Please try again.";
const NOTE_MAX_LENGTH = 500;

const SELECTORS = {
  modal: ".js-printer-queue-note-modal",
  modalDialog: ".js-printer-queue-note-modal-dialog",
  modalClose: ".js-printer-queue-note-modal-close",
  modalSubmit: ".js-printer-queue-note-modal-submit",
  modalCancel: ".js-printer-queue-note-modal-cancel",
  modalBackdrop: ".js-printer-queue-note-modal-backdrop",
  noteInput: ".js-printer-queue-note-modal-input",
  counter: ".js-printer-queue-note-modal-counter",
  error: ".js-printer-queue-note-modal-error"
};

class PrinterQueueNoteModal {
  #$modal;

  #onSubmit;

  #state = {
    shipmentId: null,
    signalType: null,
    orderId: null,
    button: null,
    lastActiveElement: null,
    isSubmitting: false,
    noteSaved: false,
    noteValueAtSave: ""
  };

  constructor({ onSubmit }) {
    this.#$modal = document.querySelector(SELECTORS.modal);
    this.#onSubmit = onSubmit;

    if (this.#$modal) {
      this.#bindEvents();
    }
  }

  get $counter() {
    return this.#$modal?.querySelector(SELECTORS.counter);
  }

  get $noteInput() {
    return this.#$modal?.querySelector(SELECTORS.noteInput);
  }

  get $modalDialog() {
    return this.#$modal?.querySelector(SELECTORS.modalDialog);
  }

  get $modalSubmit() {
    return this.#$modal?.querySelector(SELECTORS.modalSubmit);
  }

  get $modalCancel() {
    return this.#$modal?.querySelector(SELECTORS.modalCancel);
  }

  get $modalError() {
    return this.#$modal?.querySelector(SELECTORS.error);
  }

  #getFocusable() {
    return getFocusable(this.#$modal);
  }

  #setSubmitting(isSubmitting) {
    this.#state.isSubmitting = isSubmitting;
    if (this.#$modal) {
      this.#$modal.setAttribute("aria-busy", isSubmitting ? "true" : "false");
    }

    const { $noteInput, $modalSubmit, $modalCancel } = this;

    if ($modalCancel) $modalCancel.disabled = isSubmitting;
    if ($noteInput) $noteInput.disabled = isSubmitting;
    if ($modalSubmit) $modalSubmit.disabled = isSubmitting;
  }

  #setError(message) {
    const $error = this.$modalError;
    if (!$error) return;
    const { $noteInput } = this;
    if (!message) {
      $error.textContent = "";
      $error.classList.add("hidden");
      if ($noteInput) $noteInput.classList.remove("is-error");
      return;
    }
    $error.textContent = message;
    $error.classList.remove("hidden");
    if ($noteInput) $noteInput.classList.add("is-error");
  }

  #getTrimmedNote() {
    const { $noteInput } = this;
    if (!$noteInput) return "";
    return $noteInput.value.trim();
  }

  #resetForm() {
    const { $noteInput } = this;
    if ($noteInput) $noteInput.value = "";
    this.#state.noteSaved = false;
    this.#state.noteValueAtSave = "";
    this.#setError("");
    this.#updateCounter();
  }

  #updateCounter() {
    const { $counter, $noteInput } = this;
    if (!$counter) return;
    const length = $noteInput ? $noteInput.value.length : 0;
    $counter.textContent = `${length} / ${NOTE_MAX_LENGTH}`;
  }

  #handleInput() {
    this.#updateCounter();
    if (!this.#state.noteSaved) return;
    const noteValue = this.#getTrimmedNote();
    if (noteValue !== this.#state.noteValueAtSave) {
      this.#state.noteSaved = false;
    }
  }

  #handleClose(event) {
    if (this.#state.isSubmitting) return;
    if (event?.target?.closest(SELECTORS.modalBackdrop)) {
      this.close();
      return;
    }
    if (event?.target?.closest(SELECTORS.modalClose)) {
      this.close();
    }
  }

  async #handleSubmit() {
    if (this.#state.isSubmitting) return;
    if (!this.#onSubmit) return;
    this.#setError("");

    const note = this.#getTrimmedNote();
    const noteAlreadySaved = Boolean(
      this.#state.noteSaved && note && note === this.#state.noteValueAtSave
    );

    this.#setSubmitting(true);

    try {
      const result = await this.#onSubmit({
        shipmentId: this.#state.shipmentId,
        signalType: this.#state.signalType,
        orderId: this.#state.orderId,
        button: this.#state.button,
        note,
        noteAlreadySaved
      });
      if (result?.noteSaved) {
        this.#state.noteSaved = true;
        this.#state.noteValueAtSave = note;
      }
      if (result?.shouldClose) {
        this.close();
        return;
      }
      if (result?.errorMessage) {
        this.#setError(result.errorMessage);
      }
    } catch {
      this.#setError(DEFAULT_ERROR_MESSAGE);
    } finally {
      this.#setSubmitting(false);
    }
  }

  open({ shipmentId, signalType, orderId, button }) {
    if (!this.#$modal) return;
    this.#state.lastActiveElement = document.activeElement;
    this.#state.shipmentId = shipmentId;
    this.#state.signalType = signalType;
    this.#state.orderId = orderId;
    this.#state.button = button;
    this.#resetForm();
    setModalOpen({ modalEl: this.#$modal, isOpen: true, busyOnOpen: false });
    const $focusTarget = this.$noteInput || this.$modalDialog;
    $focusTarget?.focus();
  }

  close() {
    if (!this.#$modal) return;
    setModalOpen({ modalEl: this.#$modal, isOpen: false, busyOnOpen: false });
    this.#$modal.removeAttribute("aria-busy");
    const $fallback = this.#state?.lastActiveElement;
    if ($fallback && typeof $fallback.focus === "function") {
      $fallback.focus();
    }
    this.#resetForm();
  }

  #bindEvents() {
    this.#$modal?.addEventListener("click", event => this.#handleClose(event));
    this.#$modal?.addEventListener("keydown", event =>
      handleModalKeydown(event, {
        getFocusable: this.#getFocusable.bind(this),
        dialogEl: this.$modalDialog,
        onEscape: () => this.close(),
        allowEscape: () => !this.#state.isSubmitting
      })
    );
    this.$noteInput?.addEventListener("input", () => this.#handleInput());
    this.$modalSubmit?.addEventListener("click", () => this.#handleSubmit());
  }
}

const initNoteModal = ({ $container, onSubmit }) => {
  if (!$container) return null;
  return new PrinterQueueNoteModal({ onSubmit });
};

export default initNoteModal;
